/**
 * Every tool the chat agent can call, including internal tools that inherit permissions and are
 * not shown in Settings.
 *
 * `defaultPermission` mirrors the Worker's own policy — `AUTO_RUN_MCP_TOOLS` in
 * `chat-agent/src/mcp-tools.ts` — so a tool the user has said nothing about
 * behaves exactly as it did before this screen existed. A tool the catalog does
 * not name falls back to asking, so a newly advertised MCP tool cannot run
 * unattended just because nothing here describes it yet.
 */

export type ChatToolPermission = 'denied' | 'ask' | 'auto';

export type ChatToolPermissions = Record<string, ChatToolPermission>;

export const CHAT_TOOL_PERMISSIONS: ChatToolPermission[] = ['auto', 'ask', 'denied'];

export const CHAT_TOOL_PERMISSION_LABELS: Record<ChatToolPermission, string> = {
  auto: 'Auto',
  ask: 'Ask me',
  denied: 'Denied',
};

export type ChatToolGroup =
  | 'Properties and portfolios'
  | 'Imagery'
  | 'Notes'
  | 'Reports'
  | 'Lookouts'
  | 'Agent runs and detections'
  | 'Map and panels'
  | 'General';

/** Display order of the settings sections. */
export const CHAT_TOOL_GROUPS: ChatToolGroup[] = [
  'Properties and portfolios',
  'Imagery',
  'Notes',
  'Reports',
  'Lookouts',
  'Agent runs and detections',
  'Map and panels',
  'General',
];

export interface ChatTool {
  /** The bare tool name, without the `tool_lens_` namespace the Worker adds. */
  name: string;
  label: string;
  description: string;
  group: ChatToolGroup;
  defaultPermission: ChatToolPermission;
  /** False for the tools that spend money: those are only ever asked about or turned off. */
  allowAuto: boolean;
}

// Each of these commits agent-run quota or GPU minutes, so the approval card is the only thing
// standing between the model and the bill. `tool-permissions.ts` in the Worker holds the same set.
const NO_AUTO_TOOLS = new Set([
  'run_property_analysis',
  'create_run_plan',
  'start_run_plan',
  'detect_objects',
]);

const HIDDEN_FROM_SETTINGS = new Set(['batch_read']);

type CatalogEntry = [name: string, label: string, description: string];

const READS: CatalogEntry[] = [
  [
    'list_organizations',
    'List organizations',
    'Lists the organizations whose data you can read, by name.',
  ],
  ['batch_read', 'Batch reads', 'Runs several independent read tools together in one call.'],
  ['list_portfolios', 'List portfolios', 'Lists the Lens portfolios you can read.'],
  [
    'get_portfolio',
    'Read a portfolio',
    "Reads one portfolio: the properties it holds, the layers they carry, and the organization's tag names.",
  ],
  [
    'list_members',
    'List members',
    "Lists an organization's members with each one's name, email address and role.",
  ],
  [
    'list_properties',
    'List properties',
    "Lists the properties in one portfolio with each one's name, id, centroid and area.",
  ],
  [
    'search_properties',
    'Search properties',
    'Finds properties by name across every portfolio in the organization.',
  ],
  [
    'get_property',
    'Read a property',
    "Reads one property's identity and the imagery layers it holds, source by source.",
  ],
  [
    'analyze_area',
    'Analyze an area',
    'Measures how a layer moved over time across a property, or across an area drawn inside one.',
  ],
  ['list_scenes', 'List scenes', 'Lists the imagery scenes a property holds, newest first.'],
  [
    'select_orderable_scenes',
    'Find imagery to buy',
    'Finds the paid scenes one or many properties could buy that meet a request, without adding them to the cart.',
  ],
  [
    'get_scene_image',
    'View a scene',
    'Renders one scene over a property, either as a link or as pixels the agent looks at itself.',
  ],
  [
    'download_scene_png',
    'Export a scene PNG',
    'Renders one scene as the branded Lens PNG export, for a report or a record.',
  ],
  [
    'get_streaming_url',
    'Get a streaming URL',
    "Returns a WMTS URL for a property's high-resolution imagery, carrying the organization's streaming key.",
  ],
  [
    'read_uploads',
    'Read uploaded documents',
    'Reads the text of a PDF or Word document uploaded to the chat, or the rows of a CSV or Excel file.',
  ],
  [
    'list_notes',
    'List notes',
    "Lists the notes and Lookout alerts on a portfolio's properties, newest first.",
  ],
  ['get_note', 'Read a note', 'Reads one note whole: its text, outline, imagery and attachments.'],
  [
    'list_reports',
    'List reports',
    'Lists the saved reports in a portfolio, newest first, with the property each one is written on.',
  ],
  [
    'get_report_export',
    'Read a PDF export',
    'Reads a PDF export that was started earlier: whether it has finished, and the download links once it has.',
  ],
  [
    'list_public_share_links',
    'List public share links',
    "Lists the organization's live no-login links, each with the property it opens and who made it.",
  ],
  [
    'list_lookout_policies',
    'List Lookout policies',
    "Lists the Lookout policies watching an organization's portfolios and what each one measures.",
  ],
  [
    'list_lookout_alerts',
    'List Lookout alerts',
    'Lists the alerts Lookouts have raised, newest first.',
  ],
  [
    'get_lookout_policy',
    'Read a Lookout policy',
    'Reads one Lookout policy, including every property enrolled in it.',
  ],
  [
    'get_agent_run',
    'Read an analysis run',
    'Reads one analysis run: what it was asked, what imagery it read, and the observations it wrote.',
  ],
  [
    'list_agent_runs',
    'List analysis runs',
    'Lists the analysis runs on a property or across a portfolio, newest first.',
  ],
  ['list_run_plans', 'List run plans', 'Lists the run plans an organization has saved.'],
  ['get_run_plan', 'Read a run plan', 'Reads one run plan and every batch run of it.'],
  [
    'resolve_lens_url',
    'Resolve a Lens link',
    'Reads what a Lens link points at, so the ids in it can be passed to other tools.',
  ],
  [
    'find_place',
    'Find a place',
    'Looks up an address, town or county and answers with the matches and their coordinates.',
  ],
];

const WRITES: CatalogEntry[] = [
  [
    'create_note',
    'Create note',
    'Writes a new note on a property, authored by the agent and notifying whoever watches it.',
  ],
  [
    'update_note',
    'Update note',
    "Changes an existing note's text, outline, tags or imagery, replacing its text whole.",
  ],
  [
    'create_portfolio',
    'Create portfolio',
    "Creates an empty portfolio in your organization, monitored with the organization's default imagery layers.",
  ],
  [
    'create_property',
    'Create property',
    'Adds a property to a portfolio and starts imagery processing for it.',
  ],
  [
    'update_property',
    'Update property',
    "Changes a property's name, tags or assignees, replacing tags and assignees rather than adding to them.",
  ],
  [
    'update_properties',
    'Update properties',
    'Adds or removes tags and assignees on many properties at once, by id or across a whole portfolio.',
  ],
  [
    'create_tag',
    'Create tag',
    'Adds a property tag to your organization, shared by all of its portfolios.',
  ],
  [
    'update_tag',
    'Update tag',
    "Renames, recolors or reorders one of your organization's property tags.",
  ],
  [
    'delete_tag',
    'Delete tag',
    'Deletes a property tag, removing it from every property in every portfolio of your organization.',
  ],
  [
    'create_public_share_link',
    'Create public share link',
    'Creates a no-login Lens link anyone can use to view a property and one or two of its scenes.',
  ],
  [
    'delete_public_share_link',
    'Delete public share link',
    'Takes a public share link down, so its URL stops opening for everyone holding it.',
  ],
  [
    'create_report',
    'Create report',
    'Saves a new report on a property with its notes from a date range, as Lens does when you create one.',
  ],
  [
    'export_report_pdfs',
    'Export report PDFs',
    'Renders saved reports as PDFs and zips them for download, taking about a minute per report and emailing the link when it is ready.',
  ],
  [
    'create_lookout_policy',
    'Create Lookout policy',
    'Creates a Lookout policy, optionally enrolling properties in it at the same time.',
  ],
  [
    'update_lookout_policy',
    'Update Lookout policy',
    'Changes a Lookout policy; a rule passed to it replaces the rule the policy carries now.',
  ],
  [
    'archive_lookout_policy',
    'Archive Lookout policy',
    'Archives a Lookout policy so it stops watching its properties.',
  ],
  [
    'enroll_lookout_properties',
    'Enroll properties in a Lookout',
    'Enrolls whole properties in an active Lookout policy.',
  ],
  [
    'unenroll_lookout_properties',
    'Unenroll properties from a Lookout',
    'Removes every enrollment a property has in an active Lookout policy, subareas included.',
  ],
  [
    'dismiss_lookout_alert',
    'Dismiss Lookout alert',
    'Dismisses one Lookout alert, which Lens offers no way to restore.',
  ],
  [
    'describe_uploads',
    'Describe uploaded photos',
    'Sends photos uploaded to the chat to a vision model and answers with a description of each.',
  ],
];

const RUNS: CatalogEntry[] = [
  [
    'run_property_analysis',
    'Run a property analysis',
    'Runs an analysis over one property and writes what it finds as notes, spending agent-run quota and GPU minutes.',
  ],
  [
    'create_run_plan',
    'Create run plan',
    'Saves a run plan — one analysis, the properties it covers, and the imagery windows every run of it reads.',
  ],
  [
    'start_run_plan',
    'Start run plan',
    'Runs a saved plan over every property it covers, spending one agent run per property.',
  ],
  [
    'detect_objects',
    'Detect objects in imagery',
    'Counts, finds or outlines things in one scene, spending GPU minutes without leaving notes behind.',
  ],
  [
    'save_detections_as_notes',
    'Save detections as notes',
    'Keeps the footprints a detection found as notes on the property, one note per footprint.',
  ],
];

// Run on the Worker itself rather than the Lens MCP server.
const WORKER_TOOLS: CatalogEntry[] = [
  [
    'search_support_docs',
    'Search support articles',
    'Searches the Lens support articles and answers with the best matches and excerpts.',
  ],
  [
    'get_support_doc',
    'Read a support article',
    'Reads one Lens support article whole, as plain text.',
  ],
];

const CLIENT_TOOLS: CatalogEntry[] = [
  [
    'get_current_view',
    'Read the current view',
    'Reads what you are looking at in Lens: organization, portfolio, selection, layer, dates and panes.',
  ],
  ['fly_to', 'Move the map', 'Moves your map camera to a location.'],
  [
    'select_property',
    'Select a property',
    'Selects a property in the app, loading it into the map and side panels.',
  ],
  [
    'set_layer_and_date',
    'Change layer or date',
    'Changes the layer shown on the map, the date being viewed, or both.',
  ],
  [
    'open_pane',
    'Open a side pane',
    "Opens one of the app's side panes — notes, details, order or analyze.",
  ],
  [
    'draft_automation',
    'Draft an automation',
    'Opens an automation draft for you to review and save. Saves nothing itself.',
  ],
];

// Auto is offered, but not the default: the cart is a priced basket the user checks out themselves,
// so a change to it is worth seeing before it happens.
const CLIENT_WRITES: CatalogEntry[] = [
  [
    'manage_cart',
    'Manage cart',
    'Adds or removes imagery scenes in your cart. Never places orders.',
  ],
];

// The sections of the settings page, by what a tool is about rather than whether it reads or
// writes: a user deciding about Lookouts wants every Lookout tool in one place.
const TOPICS: [ChatToolGroup, string[]][] = [
  [
    'Properties and portfolios',
    [
      'list_organizations',
      'list_portfolios',
      'get_portfolio',
      'create_portfolio',
      'list_members',
      'list_properties',
      'search_properties',
      'get_property',
      'create_property',
      'update_property',
      'update_properties',
      'create_tag',
      'update_tag',
      'delete_tag',
      'create_public_share_link',
      'list_public_share_links',
      'delete_public_share_link',
    ],
  ],
  [
    'Imagery',
    [
      'list_scenes',
      'select_orderable_scenes',
      'get_scene_image',
      'download_scene_png',
      'get_streaming_url',
      'analyze_area',
      'describe_uploads',
      'read_uploads',
      'manage_cart',
    ],
  ],
  ['Notes', ['list_notes', 'get_note', 'create_note', 'update_note', 'save_detections_as_notes']],
  ['Reports', ['list_reports', 'create_report', 'export_report_pdfs', 'get_report_export']],
  [
    'Lookouts',
    [
      'list_lookout_policies',
      'get_lookout_policy',
      'list_lookout_alerts',
      'create_lookout_policy',
      'update_lookout_policy',
      'archive_lookout_policy',
      'enroll_lookout_properties',
      'unenroll_lookout_properties',
      'dismiss_lookout_alert',
    ],
  ],
  [
    'Agent runs and detections',
    [
      'list_agent_runs',
      'get_agent_run',
      'list_run_plans',
      'get_run_plan',
      'run_property_analysis',
      'create_run_plan',
      'start_run_plan',
      'detect_objects',
    ],
  ],
  [
    'Map and panels',
    [
      'get_current_view',
      'fly_to',
      'select_property',
      'set_layer_and_date',
      'open_pane',
      'draft_automation',
    ],
  ],
  [
    'General',
    ['batch_read', 'resolve_lens_url', 'find_place', 'search_support_docs', 'get_support_doc'],
  ],
];

const TOPIC_OF = new Map(TOPICS.flatMap(([group, names]) => names.map((name) => [name, group])));

function withDefault(entries: CatalogEntry[], defaultPermission: ChatToolPermission): ChatTool[] {
  return entries.map(([name, label, description]) => {
    const group = TOPIC_OF.get(name);
    if (!group) throw new Error(`${name} is in the catalog but in no section of TOPICS`);
    return {
      name,
      label,
      description,
      group,
      defaultPermission,
      allowAuto: !NO_AUTO_TOOLS.has(name),
    };
  });
}

export const CHAT_TOOL_CATALOG: ChatTool[] = [
  ...withDefault(READS, 'auto'),
  ...withDefault(WRITES, 'ask'),
  ...withDefault(RUNS, 'ask'),
  ...withDefault(WORKER_TOOLS, 'auto'),
  ...withDefault(CLIENT_TOOLS, 'auto'),
  ...withDefault(CLIENT_WRITES, 'ask'),
];

const CATALOG_BY_NAME = new Map(CHAT_TOOL_CATALOG.map((tool) => [tool.name, tool]));

export function chatTool(name: string): ChatTool | null {
  return CATALOG_BY_NAME.get(name) ?? null;
}

/** What the settings row shows, and what the browser acts on. */
export function effectivePermission(name: string, prefs: ChatToolPermissions): ChatToolPermission {
  const tool = CATALOG_BY_NAME.get(name);
  const permission = prefs[name] ?? tool?.defaultPermission ?? 'ask';
  return permission === 'auto' && tool && !tool.allowAuto ? 'ask' : permission;
}

export function permissionOptionsFor(tool: ChatTool): ChatToolPermission[] {
  return tool.allowAuto
    ? CHAT_TOOL_PERMISSIONS
    : CHAT_TOOL_PERMISSIONS.filter((permission) => permission !== 'auto');
}

/**
 * Sections in display order, each tool in the order its section lists it. `hidden` drops the tools
 * the current organization and role cannot reach, so the screen never offers a setting for a
 * feature the user has no way to use.
 */
export function catalogByGroup(
  hidden: ReadonlySet<string> = new Set()
): {group: ChatToolGroup; tools: ChatTool[]}[] {
  return TOPICS.map(([group, names]) => ({
    group,
    tools: names
      .map((name) => CATALOG_BY_NAME.get(name))
      .filter(
        (tool): tool is ChatTool =>
          !!tool && !HIDDEN_FROM_SETTINGS.has(tool.name) && !hidden.has(tool.name)
      ),
  })).filter(({tools}) => tools.length > 0);
}

/** The permission a section-wide choice stores for one tool: a tool with no Auto option gets Ask. */
export function permissionFor(tool: ChatTool, permission: ChatToolPermission): ChatToolPermission {
  return permission === 'auto' && !tool.allowAuto ? 'ask' : permission;
}
